import React, { useEffect } from 'react'
import CustomLoader from '../../custom-ui/CustomLoader';
import { getRequestForApi } from '../../utility-files/api-util/CommonRequest';
import { callHttpRequest, methodType } from '../../utility-files/api-util/HttpRequest';
import { useLocation, useNavigate } from 'react-router-dom';
import { isStringValue } from '../../utility-files/data-util/DataHandling';
import { encryptData } from '../../utility-files/data-encryption-util/DataEncryption';
import { getCurrentCoordinates } from '../../utility-files/location-util/LocationHandling';
import * as global from '../../constants/global';

const Play_Store_URL = 'https://play.google.com/store/apps/details?id=com.qed_lite';
const App_Store_URL = 'https://apps.apple.com/in/app/qedvault/id6451490560'
const IOS_PLATFORMS = ['iPhone', 'iPad', 'iPod'];

const DEVICE_UNIQUE_ID_KEY = 'deviceUniqueId';

const getCookieValue = (name) => {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
};

const setCookieValue = (name, value) => {
    const twoYearsInSeconds = 60 * 60 * 24 * 365 * 2;
    document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${twoYearsInSeconds}; path=/; SameSite=Lax`;
};

const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash).toString(36);
};

// Deterministic per-device fallback: some in-app browsers (QR scanner apps, host
// apps' built-in webviews) spin up a fresh, isolated storage sandbox on every open,
// so a stored random UUID can never survive there. A fingerprint derived from stable
// device/browser signals stays consistent for the same device even when storage doesn't.
const getDeviceFingerprint = () => {
    const nav = window.navigator || {};
    const screenInfo = window.screen || {};
    const signals = [
        nav.userAgent,
        nav.platform,
        nav.language,
        nav.hardwareConcurrency,
        nav.maxTouchPoints,
        screenInfo.width,
        screenInfo.height,
        screenInfo.colorDepth,
        new Date().getTimezoneOffset(),
    ].join('|');
    return hashString(signals);
};

const isAppleDevice = () => {
    const userAgent = window.navigator?.userAgent || '';
    const platform = window.navigator?.userAgentData?.platform || window.navigator?.platform || '';
    return /iPhone|iPad|iPod|Macintosh/i.test(userAgent) || /iPhone|iPad|iPod|Mac/i.test(platform);
};

// Dynamically imported so a Firebase/messaging failure (unsupported webview, missing
// VAPID key, no push support pre-16.4 iOS Safari) can never break this page's load.
const getFcmDeviceId = async () => {
    try {
        if (!('Notification' in window) || !('serviceWorker' in navigator)) {
            return null;
        }
        let permission = Notification.permission;
        if (permission === 'default') {
            permission = await Notification.requestPermission();
        }
        if (permission !== 'granted') {
            return null;
        }
        const { getToken } = await import('firebase/messaging');
        const { messaging } = await import('../../firebase/firebaseConfig');
        const token = await getToken(messaging, { vapidKey: process.env.REACT_APP_VAPID_KEY });
        return token || null;
    } catch (err) {
        return null;
    }
};

const getDeviceUniqueId = async () => {
    if (isAppleDevice()) {
        const fcmToken = await getFcmDeviceId();
        if (fcmToken) {
            return fcmToken;
        }
    }
    let deviceUniqueId = null;
    try {
        deviceUniqueId = window.localStorage.getItem(DEVICE_UNIQUE_ID_KEY);
    } catch (err) {
        deviceUniqueId = null;
    }
    if (!deviceUniqueId) {
        deviceUniqueId = getCookieValue(DEVICE_UNIQUE_ID_KEY);
    }
    if (!deviceUniqueId) {
        deviceUniqueId = getDeviceFingerprint();
    }
    try {
        window.localStorage.setItem(DEVICE_UNIQUE_ID_KEY, deviceUniqueId);
    } catch (err) {
        // localStorage unavailable - the cookie below is the only persisted copy
    }
    setCookieValue(DEVICE_UNIQUE_ID_KEY, deviceUniqueId);
    return deviceUniqueId;
};

const getDeviceType = () => {
    const userAgent = window.navigator.userAgent || '';
    if (window.navigator?.userAgentData?.mobile || /Mobi/i.test(userAgent)) {
        return /iPad|Tablet/i.test(userAgent) ? 'Tablet' : 'Mobile';
    }
    return 'Desktop';
};

const getBrowserName = () => {
    const userAgent = window.navigator.userAgent || '';
    if (userAgent.indexOf('OPR') !== -1) return 'Opera';
    if (userAgent.indexOf('Edg') !== -1) return 'Microsoft Edge';
    if (userAgent.indexOf('Chrome') !== -1) return 'Chrome';
    if (userAgent.indexOf('Firefox') !== -1) return 'Firefox';
    if (userAgent.indexOf('Safari') !== -1) return 'Safari';
    return window.navigator.appName;
};

const getPublicIpAddress = async () => {
    try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        return data?.ip || '';
    } catch (err) {
        return '';
    }
};

function DetectOS() {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const platform = window.navigator?.userAgentData?.platform || window.navigator.platform;

        if (location.pathname !== '/scan') {
            window.location.href = IOS_PLATFORMS.indexOf(platform) !== -1 ? App_Store_URL : Play_Store_URL;
            return;
        }

        const searchParams = new URLSearchParams(window.location.search);
        const fromUserId = searchParams.get("fromUserId");
        const assetIndex = searchParams.get("assetIndex");

        if (assetIndex) {
            callApis(assetIndex, platform);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const callApis = async (assetIndex, platform) => {
        await addAssetScanHistory(assetIndex, platform);
        await getTokenIdFromAssetIndex(assetIndex);
    };

    const getTokenIdFromAssetIndex = async (assetIndex) => {
        let variables = {
            assetIndex: isStringValue(assetIndex),
        };
        let request = getRequestForApi(global.GET_TOKEN_DETAILS_BY_ASSET_INDEX, variables, methodType.POST);
        await callHttpRequest(request).then((response) => {
            if (response?.status === 201) {
                let res = response?.data?.response?.data;
                const tokenId = res?.tokenId;
                if (tokenId) {
                    navigate(`/productDetails/${encryptData(tokenId)}`, { replace: true });
                }
            }
        }).catch((err) => {
            console.log("err", err)
        })
    };

    const addAssetScanHistory = async (assetIndex, platform) => {
        const { latitude, longitude } = await getCurrentCoordinates();
        const ipaddress = await getPublicIpAddress();
        let variables = {
            assetindex: isStringValue(assetIndex),
            consumerassetindex: '',
            latitude: isStringValue(latitude),
            longitude: isStringValue(longitude),
            ipaddress: isStringValue(ipaddress),
            deviceuniqueid: isStringValue(await getDeviceUniqueId()),
            platform: isStringValue(platform),
            devicetype: isStringValue(getDeviceType()),
            browsername: isStringValue(getBrowserName()),
        };
        let request = getRequestForApi(global.ADD_ASSET_SCAN_HISTORY, variables, methodType.POST);
        return await callHttpRequest(request).then((response) => {
            if (response?.status === 201) {
                if (response?.data?.error !== 201) {
                    // alert.show(response?.data?.response?.data || response?.data?.message, { type: alertTypes.ERROR });
                    // return false;
                }
                // return true;
            }
            // return false;
        }).catch((err) => {
            // alert.show(err?.response?.data?.message, { type: alertTypes.ERROR });
            console.log("err", err)
            // return false;
        });
    };

    return (
        <><CustomLoader /></>
    )
}

export default DetectOS
